import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, XStack, YStack } from 'tamagui';

import PSBadge from '@/components/ui/PSBadge';

/**
 * ── /components/ps-badge ─────────────────────────────────────────────────
 *
 * PSBadge across its full surface: the four variants (`brand`, `neutral`,
 * `outline`, `trial`), the two sizes (`default`, `large`) and the `disabled` dim flag.
 * Size and disabled are separate axes from variant — any look can be sized
 * up or dimmed (see the component's own docstring). Every grouping below is a real call site, not
 * an invented one:
 *   - TicketTier's per-membership tag ("Access Pass only" / "Trial Pass
 *     only" etc, `brand`, dimmed via `disabled` once that tier sells out —
 *     so the "Sold out" badge next to it owns attention)
 *   - events/[id]'s category badges ("Preview" / "PS Original", `neutral`)
 *   - EventCard's image overlay ("Sold out" / "In past", `outline`/`large`)
 *   - SecretMenuCard's and TicketListItem's own image overlays ("Sold out" /
 *     "Finished") — the SAME kind of floated overlay as EventCard's, but
 *     both reach for `neutral` instead of `outline`/`large`. That's a real
 *     inconsistency in the codebase today, not a mistake in this preview;
 *     both are shown below so it's visible rather than silently picking one.
 * ─────────────────────────────────────────────────────────────────────────
 */

export default function PSBadgePreview() {
  return (
    <>
      <Head>
        <title>PSBadge · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* The four variants side by side. `brand` is the default (no
            variant passed) — a purple chip, white label, reads as a
            highlight. `neutral` is a dark chip for a quiet/inactive state.
            `outline` is a white fill, black hairline outline, black label.
            `trial` is a pink chip, white label, for Trial Pass. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              variant — brand (default, purple) · neutral (dark) · outline
              (white, outlined) · trial (pink)
            </Text>
            <XStack gap="$md" items="center" flexWrap="wrap">
              <PSBadge label="Access Pass only" />
              <PSBadge label="Preview" variant="neutral" />
              <PSBadge label="Sold out" variant="outline" />
              <PSBadge label="Trial Pass only" variant="trial" />
            </XStack>
          </YStack>

          {/* size — `large` steps up the label ($base type) and padding
            ($lg/$sm vs $md/$xs) for a stronger call-out; any variant. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              size — default · large
            </Text>
            <XStack gap="$md" items="center" flexWrap="wrap">
              <PSBadge label="Sold out" variant="outline" />
              <PSBadge label="Sold out" variant="outline" size="large" />
            </XStack>
          </YStack>

          {/* disabled — dims whichever look is chosen via opacity, not a
            colour swap. It's a flag rather than a fourth variant because
            emphasis is orthogonal to colour: this is what TicketTier does to
            its "Access Pass only" tag once the tier itself sells out, so the
            tier's own "Sold out" badge (undimmed, see below) is the thing
            that owns the eye. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              disabled — dims via opacity (0.4), any variant; TicketTier&apos;s
              membership tag once its tier sells out
            </Text>
            <XStack gap="$md" items="center" flexWrap="wrap">
              <PSBadge label="Access Pass only" />
              <PSBadge label="Access Pass only" disabled />
            </XStack>
          </YStack>

          {/* TicketTier's real row: one `brand` badge per membership that
            unlocks the tier, wrapping onto their own line when the names
            outgrow the row (see TicketTier.tsx). Plain array of names here —
            the live component maps over `membershipsAppliedToTickettier`. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              TicketTier — one badge per required membership, undimmed while the
              tier still has stock
            </Text>
            <XStack gap="$sm" flexWrap="wrap">
              <PSBadge label="Access Pass only" />
              <PSBadge label="Trial Pass only" />
            </XStack>
          </YStack>
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              TicketTier, sold out — every membership tag dims so the large
              outline badge (undimmed) owns the row
            </Text>
            <XStack gap="$sm" items="center" flexWrap="wrap">
              <PSBadge label="Access Pass only" disabled />
              <PSBadge label="Trial Pass only" disabled />
              <PSBadge label="Sold out" variant="outline" size="large" />
            </XStack>
          </YStack>

          {/* events/[id]'s category badges — plain `neutral` labels sitting
            in-flow under the hero image (not floated), unlike every other
            grouping on this page. Mutually exclusive in the real screen (an
            event is at most one category); shown together here just to
            compare the two labels. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              events/[id] — category badge under the hero image (`neutral`,
              in-flow, not floated)
            </Text>
            <XStack gap="$md" flexWrap="wrap">
              <PSBadge label="Preview" variant="neutral" />
              <PSBadge label="PS Original" variant="neutral" />
            </XStack>
          </YStack>

          {/* EventCard's / TicketTier's image-overlay badge: `outline`
            at `large` size, floated over the bottom-left corner of a photo (here just shown
            in-flow — the overlay positioning is EventCard's job, not
            PSBadge's). events/[id]'s own hero carousel reuses this same
            look for the same two labels via ImageCarousel's
            `badgeLabel` prop. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              EventCard / hero carousel image overlay — `outline`, `large` size
            </Text>
            <XStack gap="$md" flexWrap="wrap">
              <PSBadge label="Sold out" variant="outline" size="large" />
              <PSBadge label="In past" variant="outline" size="large" />
            </XStack>
          </YStack>

          {/* SecretMenuCard and TicketListItem float the SAME kind of
            bottom-left image-corner badge as EventCard above, but both reach
            for `neutral` rather than `outline`/`large` — worth seeing side by side
            with the block above rather than treated as equivalent. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              SecretMenuCard / TicketListItem image overlay — same spot, but
              `neutral` instead of `outline`/`large`
            </Text>
            <XStack gap="$md" flexWrap="wrap">
              <PSBadge label="Sold out" variant="neutral" />
              <PSBadge label="Finished" variant="neutral" />
            </XStack>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
